import React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ActivityDetailsData } from '@/components/custom/ActivityMenu';

export interface SubNavActivityDetailsProps {
  details: ActivityDetailsData;
  isExpanded?: boolean;
  onToggle?: () => void;
  onOpenRecord?: () => void;
  variant?: 'card' | 'sheet';
}

export const SubNavActivityDetails: React.FC<SubNavActivityDetailsProps> = ({
  details,
  isExpanded = false,
  onToggle,
  onOpenRecord,
  variant = 'card'
}) => {
  if (!details) return null;

  const sectionId = React.useId();

  const renderDetails = () => (
    <>
      <div className="space-y-3">
        <div className="text-sm">
          <span className="font-medium text-gray-900">{details.action}</span>
          {details.description && (
            <span className="text-gray-600"> {details.description} </span>
          )}
          {details.patient && (
            <button className="text-sky-600 hover:text-sky-700 font-medium">
              {details.patient}
            </button>
          )}
        </div>
        <div className="text-xs text-gray-500 mt-2">
          {details.timestamp}
        </div>
        <div className="pt-3 mt-3 border-t border-gray-100 space-y-2">
          {details.location && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Location:</span>
              <span className="text-gray-700">{details.location}</span>
            </div>
          )}
          {details.date && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Date:</span>
              <span className="text-gray-700">{details.date}</span>
            </div>
          )}
          {details.provider && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Provider:</span>
              <span className="text-gray-700">{details.provider}</span>
            </div>
          )}
          {details.op && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Op:</span>
              <span className="text-gray-700">{details.op}</span>
            </div>
          )}
        </div>
      </div>
    </>
  );

  if (variant === 'card') {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-base font-semibold text-gray-900">Activity Details</h3>
          {onOpenRecord && (
            <button 
              type="button"
              onClick={onOpenRecord}
              className="text-sm text-sky-600 hover:text-sky-700 font-medium"
            >
              Open Record
            </button>
          )}
        </div>
        {renderDetails()}
      </div>
    );
  }

  // Sheet variant with collapsible header
  return (
    <div className="border-b border-gray-100">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isExpanded}
        aria-controls={sectionId}
        className="w-full flex items-center justify-between py-3 px-2 text-left hover:bg-gray-50 rounded transition-colors"
      >
        <span className="text-sm font-medium text-gray-900">Activity Details</span>
        <ChevronDown 
          className={cn(
            "w-4 h-4 text-gray-400 transition-transform",
            isExpanded && "rotate-180"
          )}
        />
      </button>
      {isExpanded && (
        <div className="pb-3 px-4" id={sectionId} role="region" aria-label="Activity Details">
          <div className="flex justify-between items-center mb-3">
            {onOpenRecord && (
              <button 
                type="button"
                onClick={onOpenRecord}
                className="text-sm text-sky-600 hover:text-sky-700 font-medium ml-auto"
              >
                Open Record
              </button>
            )}
          </div>
          {renderDetails()}
        </div>
      )}
    </div>
  );
};

SubNavActivityDetails.displayName = 'SubNavActivityDetails';
